import React from 'react';

import { Button, FocusRing, Text } from '@change/ds-core-ui';
import { CheckCircleIcon, CloseIcon, InfoIcon, WarningIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export type ToastAction = Readonly<{
	label: string | React.ReactNode;
	onClick: () => void;
	// Optional overrides for the action button; defaults preserve the original secondary-solid/small styling.
	variant?: React.ComponentProps<typeof Button>['variant'];
	size?: React.ComponentProps<typeof Button>['size'];
}>;

const variantStyles = {
	success: {
		bg: 'bg-fill-success-weaker',
		border: 'border-stroke-success-weak',
		text: 'text-typography-success',
		iconClass: 'text-icon-success',
		Icon: CheckCircleIcon,
		maxWidth: 'max-w-3xl',
		bottomPosition: 'bottom-[2vh]',
		showCloseButton: false,
	},
	error: {
		bg: 'bg-fill-error-weaker',
		border: 'border-stroke-error-weak',
		text: 'text-typography-error',
		iconClass: 'text-icon-error',
		Icon: WarningIcon,
		maxWidth: 'max-w-xl',
		bottomPosition: 'bottom-[12vh]',
		showCloseButton: true,
	},
	warning: {
		bg: 'bg-fill-warning-weaker',
		border: 'border-stroke-warning-weak',
		text: 'text-typography-warning',
		iconClass: 'text-icon-warning',
		Icon: WarningIcon,
		maxWidth: 'max-w-xl',
		bottomPosition: 'bottom-[12vh]',
		showCloseButton: true,
	},
	info: {
		bg: 'bg-fill-information-weaker',
		border: 'border-stroke-info-weak',
		text: 'text-typography-information',
		iconClass: 'text-icon-information',
		Icon: InfoIcon,
		maxWidth: 'max-w-xl',
		bottomPosition: 'bottom-[12vh] md:bottom-[13vh]',
		showCloseButton: true,
	},
} as const;

/**
 * Toast notification component that displays temporary messages to users.
 *
 * Uses event propagation stopping to prevent clicks on the toast from
 * triggering modal dismissal when rendered alongside React Aria modals.
 */
export const Toast: React.FC<Props> = ({ variant, message, action, bottomPosition, onActionClick, onClose }) => {
	const currentVariant = variantStyles[variant];
	const IconComponent = currentVariant.Icon;

	return (
		// oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- Prevent modal from closing when clicking toast
		<div
			className={cn(
				'elevation-elevated pointer-events-auto absolute left-1/2 flex w-[calc(100%-2rem)] -translate-x-1/2 flex-row items-center gap-4 rounded-md border p-6',
				bottomPosition ?? currentVariant.bottomPosition,
				currentVariant.maxWidth,
				currentVariant.bg,
				currentVariant.border,
			)}
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
			role="status"
			aria-live="polite"
			onClick={(e) => e.stopPropagation()}
			onPointerDown={(e) => e.stopPropagation()}
		>
			<div className="flex flex-1 items-center gap-4">
				<div className="flex shrink-0">
					<IconComponent className={currentVariant.iconClass} />
				</div>
				<Text className={currentVariant.text} fontWeight="bold">
					{message}
				</Text>
			</div>
			{action && (
				<Button
					// Preserve the original fixed height for the default size; let overridden sizes (e.g. x-small) size naturally.
					className={cn('shrink-0', (action.size ?? 'small') === 'small' && 'h-9')}
					onClick={onActionClick}
					variant={action.variant ?? 'secondary-solid'}
					size={action.size ?? 'small'}
				>
					{action.label}
				</Button>
			)}
			{onClose && currentVariant.showCloseButton && (
				<FocusRing>
					<button
						type="button"
						onClick={onClose}
						className={cn(
							'hover:bg-fill-press flex shrink-0 items-center justify-center rounded p-1',
							currentVariant.text,
						)}
						// FIXME: hardcoded aria-label, should use an i18n key
						aria-label="Close"
					>
						<CloseIcon className="h-5 w-5" />
					</button>
				</FocusRing>
			)}
		</div>
	);
};

type Props = Readonly<{
	variant: ToastVariant;
	message: string | React.ReactNode;
	action?: ToastAction;
	bottomPosition?: string;
	onActionClick: () => void;
	onClose?: () => void;
}>;
